<template>
  <div class="access-groups-form">
    <OurGroups class="pb-8">
      <OurInput
        v-model="form.name"
        :label="$t('promotions.name')"
        :placeholder="$t('promotions.name')"
        :error="nameError"
        :description="`${form.name.length}/${PROMOTION_NAME_MAX_LENGTH}`"
      />

      <OurGroup class="!mt-8" :title="$t('promotions.uploads')">
        <div class="promotion-uploads flex flex-col items-stretch gap-4 sm:flex-row">
          <OurFileInput
            size="lg"
            class="promotion-uploads__item flex-1"
            :label="bannerLabel"
            :files="bannerFiles"
            :allowed-file-types="bannerFileTypes"
            :disabled="!!bannerFiles.length"
            :preview="true"
            @changeFiles="(files) => onChangeFiles(files, 'banner')"
            @deleteFile="deleteFile"
          />

          <OurFileInput
            size="lg"
            class="promotion-uploads__item flex-1"
            :label="iconLabel"
            :files="iconFiles"
            :error="iconError"
            :allowed-file-types="iconFileTypes"
            :disabled="!!iconFiles.length"
            :preview="true"
            @changeFiles="(files) => onChangeFiles(files, 'icon')"
            @deleteFile="deleteFile"
          />
        </div>
      </OurGroup>

      <OurGroup class="!mt-8" :title="$t('promotions.validityPeriod')">
        <div class="flex flex-col gap-4 sm:flex-row">
          <OurDatePicker
            v-model="form.startsAt"
            class="flex-1"
            :label="$t('promotions.validityFrom')"
            :error="startsAtError"
          />

          <OurDatePicker
            v-model="form.endsAt"
            class="flex-1"
            :label="$t('promotions.validityTo')"
            :error="endsAtError"
          />
        </div>
      </OurGroup>

      <OurGroup class="!mt-8" :title="$t('promotions.description')">
        <div class="flex flex-col gap-4 lg:flex-row">
          <div>
            <OurRichEditor
              v-model="form.description"
              class="flex-1"
              :placeholder="$t('promotions.description')"
              use-custom-image-handler
              @image-added="handleImageAdded"
            />
            <p v-if="descriptionError" class="error-message">
              {{ descriptionError }}
            </p>
          </div>

          <div class="flex flex-1 flex-col gap-4">
            <OurSelect
              v-model="form.measurementUnit"
              :options="measurementUnitOptions"
              :label="$t('promotions.measurementUnit')"
              item-label="label"
              value-key="value"
              :error="measurementUnitError"
            />

            <OurInput
              v-model="form.targetValue"
              validation-rule="number"
              :label="$t('promotions.targetValue')"
              :placeholder="$t('promotions.targetValue')"
              :error="targetValueError"
            />
            <OurInput
              v-model="form.reward"
              :label="$t('promotions.reward')"
              :placeholder="$t('promotions.reward')"
              :error="rewardError"
              :description="`${form.reward.length}/${PROMOTION_REWARD_MAX_LENGTH}`"
            />
            <OurSelect
              :key="typeSelectKey"
              v-model="form.typeId"
              :options="promotionTypes"
              :label="$t('promotions.type._')"
              item-label="name"
              value-key="id"
              :error="typeIdError"
              :add-option="true"
              @addOption="onAddType"
            />
            <div class="color-field">
              <OurInput
                v-model="form.color"
                class="flex-1"
                :label="$t('promotions.color')"
                placeholder="#RRGGBB"
                :error="colorError"
                @input="onHexInput"
              />
              <input
                v-model="colorPickerValue"
                class="color-field__picker"
                type="color"
                @input="onColorPickerInput"
              />
            </div>
          </div>
        </div>
      </OurGroup>
    </OurGroups>

    <AddPromotionTypeModal :show.sync="isShownTypeModal" @created="onTypeCreated" />
  </div>
</template>

<script>
import { required, maxLength, minValue } from "vuelidate/lib/validators";
import apolloMixin from "@/mixins/apollo.mixin";
import { uploadFilesMutation } from "@/views/settings/base-knowledge/chapter/add/gql/mutations";
import { dealerIncentiveTypeListQuery } from "@/views/settings/promotions/add/gql/queries";
import AddPromotionTypeModal from "@/views/settings/promotions/add/_components/AddPromotionTypeModal.vue";
import {
  DEFAULT_MEASUREMENT_UNIT,
  HEX_COLOR_PATTERN,
  MEASUREMENT_UNIT_OPTIONS,
  PROMOTION_NAME_MAX_LENGTH,
  PROMOTION_REWARD_MAX_LENGTH,
} from "@/views/settings/promotions/add/constants";

const hexColor = (value) => HEX_COLOR_PATTERN.test(value || "");

const isRichTextEmpty = (value) => {
  if (!value) {
    return true;
  }

  return !value
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
};

const requiredRichText = (value) => !isRichTextEmpty(value);

const validDateRange = function validDateRange(endsAt) {
  const { startsAt } = this.form;

  if (!startsAt || !endsAt) {
    return true;
  }

  return Number(endsAt) >= Number(startsAt);
};

export default {
  name: "PromotionsForm",

  components: { AddPromotionTypeModal },

  mixins: [apolloMixin],

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },

    uploadFiles: {
      type: Array,
      default: () => [],
    },

    type: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      PROMOTION_NAME_MAX_LENGTH,
      PROMOTION_REWARD_MAX_LENGTH,
      bannerFileTypes: [".jpg", ".jpeg", ".png"],
      iconFileTypes: [".jpg", ".jpeg", ".png", ".svg"],
      bannerFiles: [],
      iconFiles: [],
      colorPickerValue: "#3B82F6",
      promotionTypes: [],
      typeSelectKey: 0,
      isPromotionTypesLoaded: false,
      isShownTypeModal: false,
      contentImages: [],
      form: {
        name: "",
        description: "",
        startsAt: null,
        endsAt: null,
        measurementUnit: DEFAULT_MEASUREMENT_UNIT,
        targetValue: "",
        reward: "",
        color: "#3B82F6",
        typeId: "",
        banner: null,
        icon: null,
      },
    };
  },

  validations: {
    form: {
      name: { required, maxLength: maxLength(PROMOTION_NAME_MAX_LENGTH) },
      description: { required: requiredRichText },
      startsAt: { required },
      endsAt: { required, validDateRange },
      measurementUnit: { required },
      targetValue: { required, minValue: minValue(0) },
      reward: { required, maxLength: maxLength(PROMOTION_REWARD_MAX_LENGTH) },
      color: { required, hexColor },
      typeId: { required },
    },
    iconFiles: { required },
  },

  computed: {
    bannerLabel() {
      return `${this.$t("promotions.banner")} (${this.$t("promotions.bannerHint")})`;
    },

    iconLabel() {
      return `${this.$t("promotions.icon")} (${this.$t("promotions.iconHint")})`;
    },

    measurementUnitOptions() {
      return MEASUREMENT_UNIT_OPTIONS.map((option) => ({
        value: option.value,
        label: this.$t(option.labelKey),
      }));
    },

    nameError() {
      return this.getFieldError("name", PROMOTION_NAME_MAX_LENGTH);
    },

    descriptionError() {
      if (!this.$v.form.description.$dirty) {
        return "";
      }

      if (!this.$v.form.description.required) {
        return this.$t("validation.required");
      }

      return "";
    },

    startsAtError() {
      if (!this.$v.form.startsAt.$dirty) {
        return "";
      }

      if (!this.$v.form.startsAt.required) {
        return this.$t("validation.required");
      }

      return "";
    },

    endsAtError() {
      if (!this.$v.form.endsAt.$dirty) {
        return "";
      }

      if (!this.$v.form.endsAt.required) {
        return this.$t("validation.required");
      }

      if (!this.$v.form.endsAt.validDateRange) {
        return this.$t("promotions.products.invalidDateRange");
      }

      return "";
    },

    measurementUnitError() {
      if (!this.$v.form.measurementUnit.$dirty) {
        return "";
      }

      if (!this.$v.form.measurementUnit.required) {
        return this.$t("validation.required");
      }

      return "";
    },

    targetValueError() {
      if (this.$v.form.targetValue.$dirty && !this.$v.form.targetValue.required) {
        return this.$t("validation.required");
      }

      if (this.$v.form.targetValue.$dirty && !this.$v.form.targetValue.minValue) {
        return this.$t("validation.nonNegativeNumber");
      }

      return "";
    },

    rewardError() {
      return this.getFieldError("reward", PROMOTION_REWARD_MAX_LENGTH);
    },

    colorError() {
      if (!this.$v.form.color.$dirty) {
        return "";
      }

      if (!this.$v.form.color.required) {
        return this.$t("validation.required");
      }

      if (!this.$v.form.color.hexColor) {
        return this.$t("validation.hexColor");
      }

      return "";
    },

    typeIdError() {
      if (!this.$v.form.typeId.$dirty) {
        return "";
      }

      if (!this.$v.form.typeId.required) {
        return this.$t("validation.required");
      }

      return "";
    },

    iconError() {
      if (!this.$v.iconFiles.$dirty) {
        return "";
      }

      if (!this.$v.iconFiles.required) {
        return this.$t("validation.required");
      }

      return "";
    },
  },

  watch: {
    value: {
      handler: "syncForm",
      deep: true,
      immediate: true,
    },

    uploadFiles: {
      handler: "applyUploadedFiles",
      deep: true,
    },

    bannerFiles: {
      handler: "syncBannerFile",
      deep: true,
    },

    iconFiles: {
      handler: "syncIconFile",
      deep: true,
    },
  },

  mounted() {
    this.loadPromotionTypes();
  },

  methods: {
    getFieldError(fieldName, maxLen) {
      const field = this.$v.form[fieldName];

      if (!field.$dirty) {
        return "";
      }

      if (!field.required) {
        return this.$t("validation.required");
      }

      if (field.maxLength === false) {
        return this.$t("validation.textMaxLength", { length: maxLen });
      }

      return "";
    },

    async syncForm() {
      if (!this.value || !Object.keys(this.value).length) {
        return;
      }

      this.form = { ...this.form, ...this.value };

      if (this.form.color) {
        this.colorPickerValue = this.form.color;
      }

      if (this.form.typeId) {
        this.form.typeId = String(this.form.typeId);
      }

      if (this.value.type?.id) {
        this.seedPromotionType(this.value.type);
      }

      if (this.form.typeId) {
        await this.loadPromotionTypes();
      }

      this.$nextTick(() => {
        if (this.form.banner?.id) {
          this.bannerFiles = [this.mapFile(this.form.banner)];
        }

        if (this.form.icon?.id) {
          this.iconFiles = [this.mapFile(this.form.icon)];
        }
      });
    },

    mapFile(file) {
      const { id, name, link, imageUrl } = file;

      return {
        id,
        name,
        imageUrl: imageUrl || link,
      };
    },

    seedPromotionType(type) {
      if (!type?.id) {
        return;
      }

      const exists = this.promotionTypes.some((item) => String(item.id) === String(type.id));

      if (!exists) {
        this.promotionTypes = [{ id: String(type.id), name: type.name }, ...this.promotionTypes];
        this.typeSelectKey += 1;
      }
    },

    normalizePromotionTypes(types) {
      return (types || []).map((type) => ({
        ...type,
        id: String(type.id),
      }));
    },

    async loadPromotionTypes() {
      if (this.isPromotionTypesLoaded) {
        return;
      }

      const { dealerIncentiveTypeList } = await this.$get(
        dealerIncentiveTypeListQuery,
        {},
        { withLoader: false, withNotify: false }
      );

      this.promotionTypes = this.normalizePromotionTypes(dealerIncentiveTypeList);

      if (this.value.type?.id) {
        this.seedPromotionType(this.value.type);
      }

      this.isPromotionTypesLoaded = true;
    },

    onAddType() {
      this.isShownTypeModal = true;
    },

    async onTypeCreated(type) {
      this.isPromotionTypesLoaded = false;
      await this.loadPromotionTypes();
      this.form.typeId = String(type.id);
      this.typeSelectKey += 1;
    },

    onColorPickerInput(event) {
      this.form.color = event.target.value.toUpperCase();
    },

    onHexInput(value) {
      const normalized = (value || "").trim();

      if (HEX_COLOR_PATTERN.test(normalized)) {
        this.colorPickerValue = normalized;
      }
    },

    async handleImageAdded(file, Editor, cursorLocation, resetUploader) {
      const { uploadFiles: files } = await this.$post(
        uploadFilesMutation,
        { files: file },
        { withNotify: true }
      );

      if (files?.[0]) {
        this.contentImages.push(files[0].id);
        Editor.insertEmbed(cursorLocation, "image", files[0].link);
        resetUploader();
      }
    },

    async onChangeFiles(selectedFiles, collection) {
      if (!selectedFiles?.length) {
        return;
      }

      await this.$emit("uploadFiles", { selectedFiles, type: collection });
    },

    applyUploadedFiles() {
      if (!this.uploadFiles.length) {
        return;
      }

      const mappedFile = this.mapFile(this.uploadFiles[0]);

      if (this.type === "banner") {
        this.bannerFiles = [mappedFile];
      }

      if (this.type === "icon") {
        this.iconFiles = [mappedFile];
      }
    },

    syncBannerFile() {
      this.form.banner = this.bannerFiles[0]?.id ? this.bannerFiles[0] : null;
    },

    syncIconFile() {
      this.form.icon = this.iconFiles[0]?.id ? this.iconFiles[0] : null;
    },

    async deleteFile(id) {
      await this.$emit("deleteFiles", id);

      if (this.bannerFiles.some((file) => file.id === id)) {
        this.bannerFiles = [];
      }

      if (this.iconFiles.some((file) => file.id === id)) {
        this.iconFiles = [];
      }
    },

    scrollToFirstError() {
      this.$nextTick(() => {
        const error =
          this.$el.querySelector(".error-message") ||
          this.$el.querySelector(".error") ||
          this.$el.querySelector(".error-input");

        if (error) {
          error.scrollIntoView({ behavior: "smooth", block: "end" });
        }
      });
    },

    emitForm() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.scrollToFirstError();

        return false;
      }

      const fileIds = [];
      const iconId = this.form.icon?.id || this.form.icon;
      const bannerId = this.form.banner?.id || this.form.banner;

      if (iconId) {
        fileIds.push(iconId);
      }

      if (bannerId) {
        fileIds.push(bannerId);
      }

      this.$emit("input", {
        ...this.form,
        targetValue: Number(this.form.targetValue),
        files: { sync: fileIds },
      });

      return true;
    },
  },
};
</script>

<style lang="postcss" scoped>
.error-message {
  @apply mt-1 text-sm text-red-500;
}

.color-field {
  @apply flex w-full gap-2;
}

.color-field__label {
  @apply text-sm font-medium text-gray-700;
}

.color-field__picker {
  @apply h-auto w-14 cursor-pointer rounded-lg border border-gray-300 bg-white p-0.5;
}

.promotion-uploads {
  &__item {
    @apply flex min-w-0 flex-col;

    &:deep(> div) {
      @apply flex h-full flex-1 flex-col;
    }

    &:deep(.file-upload) {
      @apply flex h-full flex-1 flex-col;
    }

    &:deep(.file-upload-block),
    &:deep(.file-upload-list) {
      @apply flex-1;
    }

    &:deep(.file-upload-button) {
      @apply mt-auto;
    }
  }
}
</style>
